import { MediaPermissionsErrorType, requestMediaPermissions } from 'mic-check';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Bowser from 'bowser';

import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useTranslation } from 'react-i18next';

const browser = Bowser.getParser(window.navigator.userAgent);
const settingsDataByOS = {
    macOS: {
        name: 'System Preferences',
        link: 'x-apple.systempreferences:com.apple.preference.security?Privacy_Camera',
    },
};

const MicCheck = () => {
    const {t:translate} = useTranslation()
    const [dialog, setDialog] = useState();
    const [errorDetails, setErrorDetails] = useState('');
    const dispatch = useDispatch();

    const checkMediaPermissions = () => {
        requestMediaPermissions({audio: true, video: false})
        .then(() => {
            // can successfully access microphone streams do nothing
            dispatch({
                type: 'startSpeechRecognition',
            });
        })
        .catch((error) => {
            if (error.type === MediaPermissionsErrorType.SystemPermissionDenied) {
                // browser does not have permission to access microphone
                setDialog('SystemPermissionDenied')
            } else if (error.type === MediaPermissionsErrorType.UserPermissionDenied) {
                // user didn't allow app to access microphone
                setDialog('UserPermissionDenied')
            } else {
                setDialog('UnhandledError')
            }
            setErrorDetails(error)
        });
    }

    const renderTryAgain = (text = translate('retry')) => {
		return (
            <button
                className='button button--alt button--tiny'
                onClick={() => {
                    window.location.reload();
                }}
            >
                {text}
            </button>
		);
	};

    const renderErrors = () => {
        if (!errorDetails) return null;
        return (
            <div className='errors-accordion'>
				<Accordion>
					<AccordionSummary
						expandIcon={<ExpandMoreRoundedIcon />}
						aria-controls="panel1a-content"
						id="panel1a-header"
					>
						<span style={{ color: 'red' }}>
							{translate('error-details')}
                        </span>
					</AccordionSummary>
					<AccordionDetails>
							<p>{errorDetails.name}: {errorDetails.message}</p>
					</AccordionDetails>
				</Accordion>
			</div>
        )
    }

    const renderSystemPermissionDenied = () => {
        return(<Fragment>
            <h4 className='mic-check__error'><span>{translate('error')}: </span>{translate('cant-use-mic')}</h4>
            <p>{translate('browser-no-access')}
            {
                // @ts-ignore
                settingsDataByOS[browser.getOSName()] ? (
                    <span
                        onClick={() => {
                            window.open(
                                // @ts-ignore
                                settingsDataByOS[browser.getOSName()]
                                    .link,
                                '_blank',
                            );
                        }}
                    >
                        {
                            // @ts-ignore
                            settingsDataByOS[browser.getOSName()].name
                        }
                    </span>
                ) : (
                    translate('settings')
                )
            }</p>
            <p>{translate('or-follow')} <a href="https://www.golingo.co.uk/how-to-allow-microphone-access/" target="_blank" rel="noreferrer" className='button button--tiny button--alt button--highlighted'>{translate('handy-guide')}</a></p>
            {renderErrors()}
            {renderTryAgain()}
        </Fragment>)
    }

    const renderUserPermissionDenied = () => {
        return (<Fragment>
            <h4 className='mic-check__error'><span>{translate('error')}: </span>{translate('mic-blocked')}</h4>
            <p>{translate('allow-access')} <a href="https://www.golingo.co.uk/how-to-allow-microphone-access/" target="_blank" rel="noreferrer" className='button button--tiny button--alt button--highlighted'>{translate('handy-guide')}</a> then {renderTryAgain()}</p>
            {renderErrors()}
        </Fragment>)
    }
    
    const renderUnhandledError = () => {
        return (<Fragment>
            <h4 className='mic-check__error'><span>{translate('error')}: </span>{translate('unexpected-error')}</h4>
            <p>{translate('allow-access')} <a href="https://www.golingo.co.uk/how-to-allow-microphone-access/" target="_blank" rel="noreferrer" className='button button--tiny button--alt button--highlighted'>{translate('handy-guide')}</a> then {renderTryAgain()}</p>
            {renderErrors()}
            {renderTryAgain()}
        </Fragment>)
    }

    const renderDialog = () => {
        switch(dialog) {
            case 'SystemPermissionDenied' :
                return renderSystemPermissionDenied();
            case 'UserPermissionDenied' :
                return renderUserPermissionDenied();
            case 'UnhandledError' :
                return renderUnhandledError();
        }
    }

    useEffect(() => {
        checkMediaPermissions();
    },[])

    return (
        <div className="mic-check">
            <div className="mic-check__content">
                {renderDialog()}
            </div>
        </div>
    )
}

export default MicCheck